import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import config from '../config';
import { getToken, readSession, writeSession } from '../auth/session';
import {
  createEmployee,
  createStandaloneTenant,
  createPayrun,
  deleteEmployee,
  export941Worksheet,
  exportW2Worksheet,
  fetch941Quarter,
  fetchComplianceOverview,
  fetchEngineCatalog,
  fetchEngineStatus,
  saveEngineSettings,
  fetchFilingsOverview,
  fetchMe,
  fetchW2Year,
  fetchW3Year,
  getEmployeeTaxProfile,
  listEmployees,
  listPayruns,
  previewPayPeriod,
  previewPayrun,
  markFiling,
  saveEmployeeTaxProfile,
  savePayPeriod,
  syncPayPeriod,
  updateEmployee,
  updateTenantSettings,
} from '../api';

const emptyEmployee = {
  displayName: '',
  email: '',
  telephone: '',
  receivesHourlyWage: false,
  hourlyRate: '',
  includeTipsAndGratuityInPayroll: '',
};

const emptyTaxProfile = {
  federalFilingStatus: 'Single',
  multipleJobs: false,
  dependentsAmount: 0,
  otherIncome: 0,
  deductions: 0,
  extraWithholding: 0,
  exemptFromFederal: false,
  employmentState: 'GA',
  gaFilingStatus: 'Single',
  gaAllowances: 0,
  gaExtraWithholding: 0,
  exemptFromState: false,
};

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start: isoDate(start), end: isoDate(end) };
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];
  const header = lines[0].toLowerCase();
  const hasHeader = header.includes('employee') || header.includes('hours') || header.includes('tip');
  const rows = hasHeader ? lines.slice(1) : lines;
  return rows.map((line) => {
    const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
    // employeeId,hours,unpaidTips[,taxableTips]
    return {
      key: parts[0] || '',
      hours: Number(parts[1] || 0) || 0,
      unpaidTips: Number(parts[2] || 0) || 0,
      taxableTips: parts[3] === undefined || parts[3] === '' ? null : (Number(parts[3]) || 0),
    };
  });
}

function areaRoute(id) {
  const map = {
    'tenants-employees': '/app/tenants',
    regulations: '/app/regulations',
    'case-data': '/app/cases',
    payrun: '/app/payrun',
    reports: '/app/reports',
    tasks: '/app/tasks',
    'developer-logs': '/app/logs',
  };
  return map[id] || '/app/engine';
}

export default function DashboardPage() {
  const token = getToken();
  const [session, setSession] = useState(readSession());
  const [tenant, setTenant] = useState(session?.tenant || null);
  const [employees, setEmployees] = useState([]);
  const [tenantType, setTenantType] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [draftEmployee, setDraftEmployee] = useState(emptyEmployee);
  const [tipsDefault, setTipsDefault] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');

  const initialRange = defaultRange();
  const [periodStart, setPeriodStart] = useState(initialRange.start);
  const [periodEnd, setPeriodEnd] = useState(initialRange.end);
  const [payRows, setPayRows] = useState([]);
  const [engine, setEngine] = useState({ connected: false, configured: false, message: '', baseUrl: '', apiKeyConfigured: false });
  const [engineDraft, setEngineDraft] = useState({ baseUrl: '', apiKey: '' });
  const [engineSaving, setEngineSaving] = useState(false);
  const [engineCatalog, setEngineCatalog] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);
  const [payMessage, setPayMessage] = useState('');
  const [payrunLines, setPayrunLines] = useState([]);
  const [payrunTotals, setPayrunTotals] = useState(null);
  const [payrunHistory, setPayrunHistory] = useState([]);
  const [payrunMessage, setPayrunMessage] = useState('');
  const [withholdingsInfo, setWithholdingsInfo] = useState({ deferred: false, source: '', message: '' });
  const [taxEditor, setTaxEditor] = useState(null); // { employeeId, displayName, profile }
  const [taxMessage, setTaxMessage] = useState('');
  const [compliance, setCompliance] = useState(null);
  const [settleUnpaidTips, setSettleUnpaidTips] = useState(true);
  const [filingYear, setFilingYear] = useState(new Date().getFullYear());
  const [filings, setFilings] = useState(null);

  async function refresh() {
    setError('');
    const me = await fetchMe(token);
    if (me.data?.status !== 'Ok') {
      throw new Error(me.data?.message || 'Session expired.');
    }
    let nextTenant = me.data.tenant;
    if (!nextTenant) {
      const created = await createStandaloneTenant(token, 'My Payroll');
      if (created.data?.status !== 'Ok') {
        throw new Error(created.data?.message || 'Could not create workspace.');
      }
      nextTenant = created.data.tenant;
    }
    setTenant(nextTenant);
    setTipsDefault(!!nextTenant.includeTipsAndGratuityInPayroll);
    setWorkspaceName(nextTenant.displayName || '');
    writeSession({ ...readSession(), username: me.data.user?.username, tenant: nextTenant });
    setSession(readSession());

    const list = await listEmployees(token);
    if (list.data?.status === 'Ok') {
      setEmployees(list.data.employees || []);
      setTenantType(list.data.tenantType);
    }

    const eng = await fetchEngineStatus(token);
    if (eng.data?.status === 'Ok') {
      const nextEngine = {
        connected: !!eng.data.connected,
        configured: !!eng.data.configured,
        message: eng.data.message || '',
        baseUrl: eng.data.baseUrl || '',
        apiKeyConfigured: !!eng.data.apiKeyConfigured,
      };
      setEngine(nextEngine);
      setEngineDraft((prev) => ({ ...prev, baseUrl: nextEngine.baseUrl || prev.baseUrl }));
    }

    const cat = await fetchEngineCatalog(token);
    if (cat.data?.status === 'Ok') {
      setEngineCatalog(cat.data);
    }
  }

  async function loadPayPeriod(start = periodStart, end = periodEnd) {
    setPayMessage('');
    const { data } = await previewPayPeriod(token, start, end);
    if (data?.status !== 'Ok') {
      throw new Error(data?.message || 'Could not load pay period.');
    }
    setPayRows(data.rows || []);
    if (data.draft) {
      setSyncInfo({
        status: data.draft.lastSyncStatus,
        message: data.draft.lastSyncMessage,
        at: data.draft.lastSyncedAtUtc,
        results: data.draft.lastSyncResults || [],
      });
    } else {
      setSyncInfo(null);
    }
  }

  async function loadCompliance(start = periodStart, end = periodEnd) {
    const { data } = await fetchComplianceOverview(token, start, end);
    if (data?.status === 'Ok') {
      setCompliance(data);
    }
  }

  async function loadFilings(year = filingYear) {
    const { data } = await fetchFilingsOverview(token, year);
    if (data?.status === 'Ok') {
      setFilings(data);
    } else {
      throw new Error(data?.message || 'Could not load filings.');
    }
  }

  async function loadPayrun(start = periodStart, end = periodEnd) {
    setPayrunMessage('');
    const preview = await previewPayrun(token, start, end);
    if (preview.data?.status !== 'Ok') {
      throw new Error(preview.data?.message || 'Could not load payrun preview.');
    }
    setPayrunLines(preview.data.lines || []);
    setPayrunTotals(preview.data.totals || null);
    setWithholdingsInfo({
      deferred: !!preview.data.withholdingsDeferred,
      source: preview.data.withholdingsSource || '',
      message: preview.data.withholdingsMessage || '',
    });
    const hist = await listPayruns(token);
    if (hist.data?.status === 'Ok') {
      setPayrunHistory(hist.data.payruns || []);
    }
  }

  async function openTaxProfile(emp) {
    const id = emp.employeeId || emp.EmployeeId;
    const name = emp.displayName || emp.DisplayName;
    setTaxMessage('');
    setBusy(true);
    setError('');
    try {
      const { data } = await getEmployeeTaxProfile(token, id);
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Could not load tax profile.');
      const p = data.profile || {};
      setTaxEditor({
        employeeId: id,
        displayName: name,
        profile: {
          ...emptyTaxProfile,
          federalFilingStatus: p.federalFilingStatus || p.FederalFilingStatus || 'Single',
          multipleJobs: !!(p.multipleJobs ?? p.MultipleJobs),
          dependentsAmount: Number(p.dependentsAmount ?? p.DependentsAmount ?? 0),
          otherIncome: Number(p.otherIncome ?? p.OtherIncome ?? 0),
          deductions: Number(p.deductions ?? p.Deductions ?? 0),
          extraWithholding: Number(p.extraWithholding ?? p.ExtraWithholding ?? 0),
          exemptFromFederal: !!(p.exemptFromFederal ?? p.ExemptFromFederal),
          employmentState: p.employmentState || p.EmploymentState || 'GA',
          gaFilingStatus: p.gaFilingStatus || p.GaFilingStatus || 'Single',
          gaAllowances: Number(p.gaAllowances ?? p.GaAllowances ?? 0),
          gaExtraWithholding: Number(p.gaExtraWithholding ?? p.GaExtraWithholding ?? 0),
          exemptFromState: !!(p.exemptFromState ?? p.ExemptFromState),
        },
      });
    } catch (err) {
      setError(err.message || 'Could not load tax profile.');
    } finally {
      setBusy(false);
    }
  }

  async function saveTaxProfile(event) {
    event.preventDefault();
    if (!taxEditor) return;
    setBusy(true);
    setTaxMessage('');
    setError('');
    try {
      const { data } = await saveEmployeeTaxProfile(token, taxEditor.employeeId, taxEditor.profile);
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Save failed.');
      setTaxMessage('Tax profile saved.');
      setTaxEditor(null);
      await refresh();
      await loadPayrun();
    } catch (err) {
      setError(err.message || 'Could not save tax profile.');
    } finally {
      setBusy(false);
    }
  }

  function downloadJson(filename, payload) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onExport941(payrunId) {
    setBusy(true);
    setError('');
    try {
      const { data } = await export941Worksheet(token, payrunId);
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Export failed.');
      downloadJson(`941-worksheet-${payrunId}.json`, data.worksheet);
    } catch (err) {
      setError(err.message || '941 export failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onExportW2(payrunId) {
    setBusy(true);
    setError('');
    try {
      const { data } = await exportW2Worksheet(token, payrunId);
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Export failed.');
      downloadJson(`w2-worksheet-${payrunId}.json`, data);
    } catch (err) {
      setError(err.message || 'W-2 export failed.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        await refresh();
        if (!cancelled) {
          await loadPayPeriod();
          await loadPayrun();
          await loadCompliance();
          await loadFilings();
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load workspace.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function saveSettings(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await updateTenantSettings(token, {
        displayName: workspaceName,
        includeTipsAndGratuityInPayroll: tipsDefault,
        payrollEnabled: true,
      });
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Save failed.');
      setTenant(data.tenant);
      writeSession({ ...readSession(), tenant: data.tenant });
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function addEmployee(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = {
        displayName: draftEmployee.displayName,
        email: draftEmployee.email,
        telephone: draftEmployee.telephone,
        receivesHourlyWage: !!draftEmployee.receivesHourlyWage,
        hourlyRate: draftEmployee.hourlyRate === '' ? null : Number(draftEmployee.hourlyRate),
        includeTipsAndGratuityInPayroll:
          draftEmployee.includeTipsAndGratuityInPayroll === ''
            ? null
            : draftEmployee.includeTipsAndGratuityInPayroll === 'true',
      };
      const { data } = await createEmployee(token, payload);
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Could not add employee.');
      setDraftEmployee(emptyEmployee);
      await refresh();
      await loadPayPeriod();
    } catch (err) {
      setError(err.message || 'Could not add employee.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleHourly(emp) {
    if (tenantType !== 'Standalone') return;
    setBusy(true);
    try {
      const { data } = await updateEmployee(token, emp.employeeId || emp.EmployeeId, {
        displayName: emp.displayName || emp.DisplayName,
        email: emp.email || emp.Email,
        telephone: emp.telephone || emp.Telephone,
        receivesHourlyWage: !(emp.receivesHourlyWage ?? emp.ReceivesHourlyWage),
        hourlyRate: emp.hourlyRate ?? emp.HourlyRate ?? 15,
        includeTipsAndGratuityInPayroll: emp.includeTipsAndGratuityInPayroll ?? emp.IncludeTipsAndGratuityInPayroll,
        active: emp.active ?? emp.Active ?? true,
      });
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Update failed.');
      await refresh();
      await loadPayPeriod();
    } catch (err) {
      setError(err.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeEmployee(emp) {
    if (tenantType !== 'Standalone') return;
    if (!window.confirm(`Remove ${emp.displayName || emp.DisplayName}?`)) return;
    setBusy(true);
    try {
      await deleteEmployee(token, emp.employeeId || emp.EmployeeId);
      await refresh();
      await loadPayPeriod();
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  function updateRow(employeeId, patch) {
    setPayRows((prev) => prev.map((r) => (
      r.employeeId === employeeId ? { ...r, ...patch } : r
    )));
  }

  async function onRefreshPeriod() {
    setBusy(true);
    setError('');
    try {
      await loadPayPeriod(periodStart, periodEnd);
      await loadPayrun(periodStart, periodEnd);
      await loadCompliance(periodStart, periodEnd);
      setPayMessage('Pay period refreshed.');
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveDraft() {
    setBusy(true);
    setError('');
    setPayMessage('');
    try {
      const { data } = await savePayPeriod(token, {
        startDate: periodStart,
        endDate: periodEnd,
        rows: payRows,
      });
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Save failed.');
      setPayMessage('Draft saved.');
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onSync() {
    setBusy(true);
    setError('');
    setPayMessage('');
    try {
      // Save draft first so Standalone edits persist even if PE is down.
      await savePayPeriod(token, {
        startDate: periodStart,
        endDate: periodEnd,
        rows: payRows,
      });
      const { data } = await syncPayPeriod(token, {
        startDate: periodStart,
        endDate: periodEnd,
        rows: payRows,
      });
      setSyncInfo({
        status: data?.draft?.lastSyncStatus || (data?.status === 'Ok' ? 'Ok' : 'Error'),
        message: data?.message || data?.draft?.lastSyncMessage,
        at: data?.draft?.lastSyncedAtUtc,
        results: data?.results || data?.draft?.lastSyncResults || [],
      });
      if (data?.status === 'Ok') {
        setPayMessage(data.message || 'Synced to Payroll Engine.');
      } else {
        setPayMessage(data?.message || 'Sync did not complete.');
      }
      const eng = await fetchEngineStatus(token);
      if (eng.data?.status === 'Ok') {
        setEngine({
          connected: !!eng.data.connected,
          configured: !!eng.data.configured,
          message: eng.data.message || '',
          baseUrl: eng.data.baseUrl || '',
          apiKeyConfigured: !!eng.data.apiKeyConfigured,
        });
      }
    } catch (err) {
      setError(err.message || 'Sync failed.');
    } finally {
      setBusy(false);
    }
  }

  function onCsvFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result || ''));
        if (parsed.length === 0) {
          setError('CSV had no data rows. Expected: employeeId,hours,unpaidTips[,taxableTips]');
          return;
        }
        setPayRows((prev) => {
          const next = prev.map((r) => ({ ...r }));
          for (const row of parsed) {
            const match = next.find((r) =>
              String(r.employeeId).toLowerCase() === row.key.toLowerCase()
              || String(r.displayName || '').toLowerCase() === row.key.toLowerCase());
            if (!match) continue;
            if (match.receivesHourlyWage && !match.hoursFromBistro) {
              match.hours = row.hours;
            }
            if (match.includeTips && !match.tipsFromBistro) {
              match.unpaidTips = row.unpaidTips;
              if (row.taxableTips != null) {
                match.taxableTips = row.taxableTips;
              } else if ((match.taxableTips || 0) < row.unpaidTips) {
                match.taxableTips = row.unpaidTips;
              }
              match.tipsAlreadyPaid = Math.max(0, (match.taxableTips || 0) - (match.unpaidTips || 0));
            }
          }
          return next;
        });
        setPayMessage(`Imported ${parsed.length} CSV row(s). Save draft to keep.`);
      } catch (err) {
        setError(err.message || 'CSV import failed.');
      }
    };
    reader.readAsText(file);
  }

  async function onCreatePayrun(finalize) {
    setBusy(true);
    setError('');
    setPayrunMessage('');
    try {
      await savePayPeriod(token, {
        startDate: periodStart,
        endDate: periodEnd,
        rows: payRows,
      });
      const { data } = await createPayrun(token, {
        startDate: periodStart,
        endDate: periodEnd,
        rows: payRows,
        finalize: !!finalize,
        settleUnpaidTips: finalize ? settleUnpaidTips : false,
      });
      if (data?.status !== 'Ok') throw new Error(data?.message || 'Payrun failed.');
      setPayrunMessage(data.message || (finalize ? 'Payrun finalized.' : 'Payrun draft saved.'));
      await loadPayrun(periodStart, periodEnd);
      await loadCompliance(periodStart, periodEnd);
    } catch (err) {
      setError(err.message || 'Payrun failed.');
    } finally {
      setBusy(false);
    }
  }

  const location = useLocation();
  const section = (location.pathname.replace(/\/$/, '') || '/app') === '/app'
    ? 'overview'
    : location.pathname.replace(/^\/app\//, '');
  const isStandalone = tenantType === 'Standalone' || tenant?.tenantType === 'Standalone';
  const engineClass = engine.connected ? 'status-ok' : 'status-bad';
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const titles = {
    overview: tenant?.displayName || 'Payroll workspace',
    'pay-period': 'Pay period',
    payrun: 'Payrun',
    filings: 'Filings',
    people: 'Employees',
    engine: 'Payroll Engine',
    regulations: 'Regulations',
    cases: 'Case data',
    reports: 'Reports',
    tasks: 'Tasks',
    logs: 'Developer logs',
    settings: 'Settings',
  };

  return (
    <>
      <span className="badge">Phase 6 · {tenant?.tenantType || tenantType || '…'}</span>
      <h1 className="hero-title" style={{ fontSize: '1.85rem' }}>
        {titles[section] || tenant?.displayName || 'Payroll workspace'}
      </h1>
      {section === 'overview' ? (
        <p className="lede">
          Signed in as {session?.username || '…'}.
          {isStandalone
            ? ' Standalone mode — manage employees and enter hours/tips here.'
            : ' Bistro-linked — hours and tips load from Bistro for the selected period.'}
          {' '}Taxable tips include all tips (T6); paycheck tip cash is unpaid only. Merchant is employer of record.
        </p>
      ) : null}
      <p className={`muted engine-pill ${engineClass}`}>
        Engine: {engine.connected ? 'Connected' : (engine.configured ? 'Configured but unreachable' : 'Not configured')}
        {engine.message ? ` — ${engine.message}` : ''}
      </p>
      {error ? <p className="error">{error}</p> : null}
      {busy ? <p className="muted">Working…</p> : null}

      {section === 'engine' ? (
      <>
      <section className="panel">
        <h2>Configure Payroll Engine</h2>
        <p className="muted">
          Private calculation backend. POSApi calls this URL — the browser does not.
          Shared API hosts cannot reach localhost on your laptop.
        </p>
        <div className="pay-toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
          <label>
            Base URL
            <input
              value={engineDraft.baseUrl}
              onChange={(e) => setEngineDraft((prev) => ({ ...prev, baseUrl: e.target.value }))}
              placeholder="http://localhost:5001"
              disabled={engineSaving || busy}
            />
          </label>
          <label>
            API key (optional)
            <input
              type="password"
              autoComplete="off"
              value={engineDraft.apiKey}
              onChange={(e) => setEngineDraft((prev) => ({ ...prev, apiKey: e.target.value }))}
              placeholder={engine.apiKeyConfigured ? 'Saved — leave blank to keep' : 'Optional'}
              disabled={engineSaving || busy}
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={engineSaving || busy}
            onClick={async () => {
              setEngineSaving(true);
              setError('');
              try {
                const payload = { BaseUrl: (engineDraft.baseUrl || '').trim() };
                if ((engineDraft.apiKey || '').trim()) {
                  payload.ApiKey = engineDraft.apiKey.trim();
                }
                const { data } = await saveEngineSettings(token, payload);
                if (data?.status !== 'Ok') {
                  throw new Error(data?.message || 'Could not save Payroll Engine settings.');
                }
                setEngine({
                  connected: !!data.connected,
                  configured: !!data.configured,
                  message: data.message || '',
                  baseUrl: data.baseUrl || '',
                  apiKeyConfigured: !!data.apiKeyConfigured,
                });
                setEngineDraft({ baseUrl: data.baseUrl || '', apiKey: '' });
              } catch (err) {
                setError(err.message || 'Payroll Engine save failed.');
              } finally {
                setEngineSaving(false);
              }
            }}
          >
            {engineSaving ? 'Connecting…' : 'Save & test connection'}
          </button>
        </div>
      </section>
      {engineCatalog?.areas?.length ? (
        <section className="panel">
          <h2>Payroll Engine areas (PE-0)</h2>
          <p className="muted">
            Native Rebelity UI via POSApi proxy
            {' '}<code>{engineCatalog.proxyBase}</code>
            {engineCatalog.peApiBase ? ` → ${engineCatalog.peApiBase}` : ''}.
          </p>
          <div className="nav-tiles" style={{ marginTop: 16 }}>
            {engineCatalog.areas.map((area) => (
              <Link key={area.id} to={areaRoute(area.id)}>
                {area.title}
                <span className="muted" style={{ display: 'block', fontWeight: 500 }}>
                  {(area.pePrefixes || []).length} PE routes
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      </>
      ) : null}

      {section === 'overview' ? (
      <>
      <section className="panel">
        <h2>Jump to</h2>
        <div className="nav-tiles">
          <Link to="/app/pay-period">Pay period</Link>
          <Link to="/app/payrun">Payrun</Link>
          <Link to="/app/people">Employees</Link>
          <Link to="/app/filings">Filings</Link>
          <Link to="/app/engine">Engine connection</Link>
          <Link to="/app/tenants">Tenants</Link>
          <Link to="/app/settings">Settings</Link>
        </div>
      </section>
      <section className="panel">
        <h2>Compliance overview</h2>
        <p className="muted">
          Bridge view: tips owed, tax reserved, payroll liabilities, and engine status.
          Finalize marks unpaid tips as paid (no Stripe transfer) when linked to Bistro.
        </p>
        <div className="pay-toolbar">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await loadCompliance();
              } catch (err) {
                setError(err.message || 'Compliance refresh failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Refresh compliance
          </button>
        </div>
        {compliance ? (
          <div className="totals-row" style={{ marginTop: 16 }}>
            <div>
              <span className="muted">Tips owed</span>
              <strong> {money(compliance.tipsOwed?.amount)}</strong>
              <div className="muted">{compliance.tipsOwed?.message}</div>
            </div>
            <div>
              <span className="muted">Tax reserved (pool)</span>
              <strong> {money(compliance.taxReserved?.taxCollectedAmount ?? compliance.taxReserved?.stripeTaxLedgerNet)}</strong>
              <div className="muted">
                {compliance.taxReserved?.taxCollectedSource || '—'}
                {compliance.taxReserved?.taxReserveAmount != null
                  ? ` · hold ${money(compliance.taxReserved.taxReserveAmount)}`
                  : ''}
              </div>
            </div>
            <div>
              <span className="muted">EE withholdings</span>
              <strong> {money(compliance.payrollLiabilities?.totalEmployeeWithholdings)}</strong>
            </div>
            <div>
              <span className="muted">ER taxes</span>
              <strong> {money(compliance.payrollLiabilities?.totalEmployerTaxes)}</strong>
            </div>
            <div>
              <span className="muted">Tips settled via payroll</span>
              <strong> {money(compliance.payrollLiabilities?.tipsSettledViaPayroll)}</strong>
            </div>
            <div>
              <span className="muted">Payroll Engine</span>
              <strong className={compliance.payrollEngine?.connected ? 'status-ok' : 'status-bad'}>
                {' '}
                {compliance.payrollEngine?.connected ? 'Connected' : 'Offline'}
              </strong>
              <div className="muted">{compliance.payrollEngine?.message}</div>
            </div>
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>Loading compliance…</p>
        )}
        {compliance?.safeguards ? (
          <p className="muted" style={{ marginTop: 12 }}>
            Safeguards: taxable tips = {compliance.safeguards.taxableTips}; payable = {compliance.safeguards.payableTips}.
            {' '}{compliance.safeguards.disclaimer}
          </p>
        ) : null}
      </section>
      </>
      ) : null}

      {section === 'filings' ? (
      <section className="panel">
        <h2>Filings</h2>
        <p className="muted">
          Deposit and file as the employer. Rebelity prepares worksheets and tracks status — it does not remit to the IRS, Georgia DOR, or Stripe Tax.
        </p>
        <div className="pay-toolbar">
          <label>
            Tax year
            <input
              type="number"
              min="2024"
              max="2099"
              value={filingYear}
              onChange={(e) => setFilingYear(Number(e.target.value) || new Date().getFullYear())}
            />
          </label>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await loadFilings(filingYear);
              } catch (err) {
                setError(err.message || 'Filings refresh failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Refresh filings
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await fetch941Quarter(token, filingYear, 1);
                if (data?.status !== 'Ok') throw new Error(data?.message || '941 export failed.');
                downloadJson(`941-Q1-${filingYear}.json`, data.worksheet);
              } catch (err) {
                setError(err.message || '941 export failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            941 Q1
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await fetchW2Year(token, filingYear);
                if (data?.status !== 'Ok') throw new Error(data?.message || 'W-2 export failed.');
                downloadJson(`w2-${filingYear}.json`, data);
              } catch (err) {
                setError(err.message || 'W-2 export failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            W-2 YTD
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await fetchW3Year(token, filingYear);
                if (data?.status !== 'Ok') throw new Error(data?.message || 'W-3 export failed.');
                downloadJson(`w3-${filingYear}.json`, data.worksheet);
              } catch (err) {
                setError(err.message || 'W-3 export failed.');
              } finally {
                setBusy(false);
              }
            }}
          >
            W-3
          </button>
        </div>
        {filings?.remitter ? <p className="muted" style={{ marginTop: 10 }}>{filings.remitter}</p> : null}
        {filings?.partner?.message ? <p className="muted">{filings.partner.message}</p> : null}
        {Array.isArray(filings?.obligations) && filings.obligations.length > 0 ? (
          <table className="pay-grid" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Filing</th>
                <th>Due</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filings.obligations.map((ob) => {
                const id = ob.obligationId || ob.ObligationId;
                const status = ob.status || ob.Status || 'Due';
                return (
                  <tr key={id}>
                    <td>
                      <strong>{ob.label || ob.Label}</strong>
                      <div className="muted">{ob.notes || ob.Notes}</div>
                      {(ob.externalUrl || ob.ExternalUrl) ? (
                        <div>
                          <a href={ob.externalUrl || ob.ExternalUrl} target="_blank" rel="noreferrer">Instructions</a>
                        </div>
                      ) : null}
                    </td>
                    <td>{ob.dueDate || ob.DueDate}</td>
                    <td>{money(ob.amountDue ?? ob.AmountDue)}</td>
                    <td>{status}</td>
                    <td>
                      {status !== 'Filed' ? (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            setError('');
                            try {
                              const { data } = await markFiling(token, id, {
                                status: 'Filed',
                                taxYear: filingYear,
                              });
                              if (data?.status !== 'Ok') throw new Error(data?.message || 'Update failed.');
                              await loadFilings(filingYear);
                            } catch (err) {
                              setError(err.message || 'Could not mark filing.');
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Mark filed
                        </button>
                      ) : (
                        <span className="status-ok">Filed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>Refresh filings to load the {filingYear} calendar.</p>
        )}
      </section>
      ) : null}

      {section === 'pay-period' ? (
      <section className="panel">
        <h2>Pay period</h2>
        <p className="muted">
          Preview hours and tips, then sync to Payroll Engine or build a payrun.
          Taxable tips = all tips; payable tips = unpaid only.
        </p>
        <div className="pay-toolbar">
          <label>
            Start
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </label>
          <label>
            End
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </label>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={onRefreshPeriod}>
            Refresh
          </button>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={onSaveDraft}>
            Save draft
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={onSync}>
            Sync to payroll engine
          </button>
          {isStandalone ? (
            <label>
              Import CSV
              <input type="file" accept=".csv,text/csv" onChange={onCsvFile} />
            </label>
          ) : null}
        </div>
        {isStandalone ? (
          <p className="muted" style={{ marginTop: 8 }}>
            CSV: <code>employeeId,hours,unpaidTips[,taxableTips]</code>
          </p>
        ) : null}
        {payMessage ? <p className="muted" style={{ marginTop: 10 }}>{payMessage}</p> : null}

        {payRows.length === 0 ? (
          <p className="muted" style={{ marginTop: 16 }}>
            No employees in this period (need hourly and/or tips-in-payroll enabled).
          </p>
        ) : (
          <table className="pay-grid">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Hours</th>
                <th>Taxable tips</th>
                <th>Payable tips</th>
                <th>Already paid</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {payRows.map((row) => {
                const hoursEditable = isStandalone && row.receivesHourlyWage && !row.hoursFromBistro;
                const tipsEditable = isStandalone && row.includeTips && !row.tipsFromBistro;
                return (
                  <tr key={row.employeeId}>
                    <td>
                      <strong>{row.displayName}</strong>
                      <div className="muted">{row.employeeId}</div>
                    </td>
                    <td>
                      {hoursEditable ? (
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={row.hours}
                          onChange={(e) => updateRow(row.employeeId, { hours: Number(e.target.value) || 0 })}
                        />
                      ) : (
                        <span>{Number(row.hours || 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td>
                      {tipsEditable ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.taxableTips ?? row.unpaidTips ?? 0}
                          onChange={(e) => {
                            const taxableTips = Number(e.target.value) || 0;
                            const unpaidTips = row.unpaidTips || 0;
                            updateRow(row.employeeId, {
                              taxableTips,
                              tipsAlreadyPaid: Math.max(0, taxableTips - unpaidTips),
                            });
                          }}
                        />
                      ) : (
                        <span>{money(row.taxableTips)}</span>
                      )}
                    </td>
                    <td>
                      {tipsEditable ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.unpaidTips}
                          onChange={(e) => {
                            const unpaidTips = Number(e.target.value) || 0;
                            const taxableTips = Math.max(row.taxableTips || 0, unpaidTips);
                            updateRow(row.employeeId, {
                              unpaidTips,
                              taxableTips,
                              tipsAlreadyPaid: Math.max(0, taxableTips - unpaidTips),
                            });
                          }}
                        />
                      ) : (
                        <span>{money(row.unpaidTips)}</span>
                      )}
                    </td>
                    <td>{money(row.tipsAlreadyPaid)}</td>
                    <td className="muted">
                      {row.receivesHourlyWage ? 'Hourly' : 'Non-hourly'}
                      {row.includeTips ? ' · Tips' : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {syncInfo ? (
          <div className="sync-results">
            <p className={syncInfo.status === 'Ok' ? 'status-ok' : 'status-bad'}>
              Last sync: {syncInfo.status || '—'}
              {syncInfo.message ? ` — ${syncInfo.message}` : ''}
              {syncInfo.at ? ` (${new Date(syncInfo.at).toLocaleString()})` : ''}
            </p>
            {Array.isArray(syncInfo.results) && syncInfo.results.length > 0 ? (
              <ul>
                {syncInfo.results.map((r, i) => (
                  <li key={`${r.employeeId || i}-${i}`} className={r.success ? 'status-ok' : 'status-bad'}>
                    {r.displayName || r.employeeId || 'Row'}: {r.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>
      ) : null}

      {section === 'payrun' ? (
      <section className="panel">
        <h2>Payrun</h2>
        <p className="muted">
          Gross = wages + tips. Employee withholdings (FIT / FICA / GA) reduce net from payable gross.
          Already-paid tips stay taxable but are not paid again.
        </p>
        <div className="pay-toolbar">
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onCreatePayrun(false)}>
            Save payrun draft
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onCreatePayrun(true)}>
            Finalize payrun
          </button>
        </div>
        {!isStandalone ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <input
              type="checkbox"
              checked={settleUnpaidTips}
              onChange={(e) => setSettleUnpaidTips(e.target.checked)}
            />
            On finalize: mark unpaid tips in this period as paid (skip Stripe transfer)
          </label>
        ) : null}
        {payrunMessage ? <p className="muted" style={{ marginTop: 10 }}>{payrunMessage}</p> : null}
        {withholdingsInfo.message ? (
          <p className={withholdingsInfo.deferred ? 'status-bad' : 'status-ok'} style={{ marginTop: 8 }}>
            Withholdings ({withholdingsInfo.source || '—'}): {withholdingsInfo.message}
          </p>
        ) : null}

        {payrunTotals ? (
          <div className="totals-row" style={{ marginTop: 16 }}>
            <div><span className="muted">Wages</span><strong> {money(payrunTotals.totalWageAmount ?? payrunTotals.TotalWageAmount)}</strong></div>
            <div><span className="muted">Taxable tips</span><strong> {money(payrunTotals.totalTaxableTips ?? payrunTotals.TotalTaxableTips)}</strong></div>
            <div><span className="muted">Payable tips</span><strong> {money(payrunTotals.totalUnpaidTips ?? payrunTotals.TotalUnpaidTips)}</strong></div>
            <div><span className="muted">Taxable gross</span><strong> {money(payrunTotals.totalTaxableGross ?? payrunTotals.TotalTaxableGross)}</strong></div>
            <div><span className="muted">Payable gross</span><strong> {money(payrunTotals.totalPayableGross ?? payrunTotals.TotalPayableGross)}</strong></div>
            <div><span className="muted">EE withholdings</span><strong> {money(payrunTotals.totalWithholdings ?? payrunTotals.TotalWithholdings)}</strong></div>
            <div><span className="muted">ER taxes</span><strong> {money(payrunTotals.totalEmployerTaxes ?? payrunTotals.TotalEmployerTaxes)}</strong></div>
            <div><span className="muted">Net pay</span><strong> {money(payrunTotals.totalNetPay ?? payrunTotals.TotalNetPay)}</strong></div>
          </div>
        ) : null}

        {payrunLines.length > 0 ? (
          <table className="pay-grid">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Taxable gross</th>
                <th>Payable gross</th>
                <th>FIT</th>
                <th>FICA EE</th>
                <th>GA</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {payrunLines.map((line) => (
                <tr key={line.employeeId}>
                  <td>
                    {line.displayName}
                    {!line.hasTaxProfile ? <div className="muted">Default profile</div> : null}
                  </td>
                  <td>{money(line.taxableGross)}</td>
                  <td>{money(line.payableGross)}</td>
                  <td>{money(line.federalWithholding)}</td>
                  <td>{money(line.ficaEmployee)}</td>
                  <td>{money(line.gaWithholding)}</td>
                  <td>{money(line.netPay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>Refresh the pay period to load payrun lines.</p>
        )}

        {payrunHistory.length > 0 ? (
          <div className="sync-results">
            <p className="muted">Recent payruns</p>
            <ul>
              {payrunHistory.slice(0, 8).map((p) => {
                const id = p.payrunId || p.PayrunId;
                return (
                  <li key={id}>
                    {(p.startDate || p.StartDate)} → {(p.endDate || p.EndDate)}
                    {' · '}
                    {p.status || p.Status}
                    {' · '}
                    taxable {money(p.totalTaxableGross ?? p.TotalTaxableGross)}
                    {' / net '}
                    {money(p.totalNetPay ?? p.TotalNetPay)}
                    {' · '}
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onExport941(id)}>
                      941 stub
                    </button>
                    {' '}
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onExportW2(id)}>
                      W-2 stub
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>
      ) : null}

      {section === 'settings' ? (
      <section className="panel">
        <h2>Workspace settings</h2>
        <form className="form" onSubmit={saveSettings}>
          <label>
            Display name
            <input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={tipsDefault}
              onChange={(e) => setTipsDefault(e.target.checked)}
            />
            Include unpaid tips &amp; gratuity in payroll (default)
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy}>Save settings</button>
        </form>
      </section>
      ) : null}

      {section === 'people' ? (
      <>
      <section className="panel">
        <h2>Employees</h2>
        {employees.length === 0 ? (
          <p className="muted">No employees yet.</p>
        ) : (
          <div className="table">
            {employees.map((emp) => {
              const id = emp.employeeId || emp.EmployeeId;
              const name = emp.displayName || emp.DisplayName;
              const hourly = !!(emp.receivesHourlyWage ?? emp.ReceivesHourlyWage);
              const rate = emp.hourlyRate ?? emp.HourlyRate;
              return (
                <div className="row" key={id}>
                  <div>
                    <strong>{name}</strong>
                    <div className="muted">{emp.email || emp.Email || '—'}</div>
                    <div className="muted">
                      {(emp.hasTaxProfile ?? emp.HasTaxProfile) ? 'W-4 / G-4 on file' : 'Tax profile: defaults'}
                    </div>
                  </div>
                  <div className="muted">
                    {hourly ? `Hourly · $${Number(rate || 0).toFixed(2)}` : 'Non-hourly'}
                  </div>
                  <div className="row-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => openTaxProfile(emp)}>
                      Tax profile
                    </button>
                    {isStandalone ? (
                      <>
                        <button type="button" className="btn btn-ghost" onClick={() => toggleHourly(emp)}>
                          Toggle hourly
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => removeEmployee(emp)}>
                          Remove
                        </button>
                      </>
                    ) : (
                      <span className="muted">Roster from Bistro</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isStandalone ? (
          <form className="form" style={{ marginTop: 20 }} onSubmit={addEmployee}>
            <h2 style={{ margin: 0 }}>Add employee</h2>
            <label>
              Name
              <input
                required
                value={draftEmployee.displayName}
                onChange={(e) => setDraftEmployee({ ...draftEmployee, displayName: e.target.value })}
              />
            </label>
            <label>
              Email
              <input
                value={draftEmployee.email}
                onChange={(e) => setDraftEmployee({ ...draftEmployee, email: e.target.value })}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={draftEmployee.receivesHourlyWage}
                onChange={(e) => setDraftEmployee({ ...draftEmployee, receivesHourlyWage: e.target.checked })}
              />
              Receives hourly wage
            </label>
            {draftEmployee.receivesHourlyWage ? (
              <label>
                Hourly rate
                <input
                  required
                  inputMode="decimal"
                  value={draftEmployee.hourlyRate}
                  onChange={(e) => setDraftEmployee({ ...draftEmployee, hourlyRate: e.target.value })}
                />
              </label>
            ) : null}
            <label>
              Tips in payroll
              <select
                value={draftEmployee.includeTipsAndGratuityInPayroll}
                onChange={(e) => setDraftEmployee({ ...draftEmployee, includeTipsAndGratuityInPayroll: e.target.value })}
              >
                <option value="">Use workspace default</option>
                <option value="true">Always include</option>
                <option value="false">Never include</option>
              </select>
            </label>
            <button className="btn btn-primary" type="submit" disabled={busy}>Add employee</button>
          </form>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>
            Manage Bistro staff under{' '}
            <a href={`${config.bistroAdminUrl}/#/admin/users`}>BistroAdmin → Users</a>.
            {' '}Add Payroll Engine employees under{' '}
            <Link to="/app/tenants">Engine → Tenants</Link>
            {' '}→ Employees → <strong>Add employee</strong> (native; Import roster optional).
          </p>
        )}
        {isStandalone ? (
          <p className="muted" style={{ marginTop: 12 }}>
            People roster is separate from the PE calculation roster. Add PE employees under{' '}
            <Link to="/app/tenants">Engine → Tenants</Link>
            {' '}→ Employees → Add employee, or optionally Import Rebelity roster.
          </p>
        ) : null}
      </section>

      {taxEditor ? (
        <div className="panel" style={{ borderColor: 'rgba(255,255,255,0.25)' }}>
          <h2>Tax profile — {taxEditor.displayName}</h2>
          <p className="muted">Federal W-4 (2020+) and Georgia G-4 fields. Merchant remains employer of record.</p>
          {taxMessage ? <p className="status-ok">{taxMessage}</p> : null}
          <form className="form" onSubmit={saveTaxProfile}>
            <label>
              Federal filing status
              <select
                value={taxEditor.profile.federalFilingStatus}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, federalFilingStatus: e.target.value },
                })}
              >
                <option value="Single">Single / Married filing separately</option>
                <option value="MarriedFilingJointly">Married filing jointly</option>
                <option value="HeadOfHousehold">Head of household</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={taxEditor.profile.multipleJobs}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, multipleJobs: e.target.checked },
                })}
              />
              Multiple jobs (W-4 step 2c)
            </label>
            <label>
              Dependents amount (step 3)
              <input
                type="number"
                min="0"
                step="0.01"
                value={taxEditor.profile.dependentsAmount}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, dependentsAmount: Number(e.target.value) || 0 },
                })}
              />
            </label>
            <label>
              Extra federal withholding
              <input
                type="number"
                min="0"
                step="0.01"
                value={taxEditor.profile.extraWithholding}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, extraWithholding: Number(e.target.value) || 0 },
                })}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={taxEditor.profile.exemptFromFederal}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, exemptFromFederal: e.target.checked },
                })}
              />
              Exempt from federal withholding
            </label>
            <label>
              GA filing status
              <select
                value={taxEditor.profile.gaFilingStatus}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, gaFilingStatus: e.target.value },
                })}
              >
                <option value="Single">Single</option>
                <option value="MarriedFilingJointly">Married filing jointly</option>
                <option value="MarriedFilingSeparately">Married filing separately</option>
                <option value="HeadOfHousehold">Head of household</option>
              </select>
            </label>
            <label>
              GA allowances
              <input
                type="number"
                min="0"
                step="1"
                value={taxEditor.profile.gaAllowances}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, gaAllowances: Number(e.target.value) || 0 },
                })}
              />
            </label>
            <label>
              Extra GA withholding
              <input
                type="number"
                min="0"
                step="0.01"
                value={taxEditor.profile.gaExtraWithholding}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, gaExtraWithholding: Number(e.target.value) || 0 },
                })}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={taxEditor.profile.exemptFromState}
                onChange={(e) => setTaxEditor({
                  ...taxEditor,
                  profile: { ...taxEditor.profile, exemptFromState: e.target.checked },
                })}
              />
              Exempt from Georgia withholding
            </label>
            <div className="pay-toolbar">
              <button className="btn btn-primary" type="submit" disabled={busy}>Save tax profile</button>
              <button className="btn btn-ghost" type="button" onClick={() => setTaxEditor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      ) : null}
      </>
      ) : null}

    </>
  );
}
